import { MODE_BY_NUMBER } from '../../../constants/cycle';
import { ChannelState } from '../../../enums/channel-state.enum';
import CycleSettings from '../../../enums/cycle-setting.enum';
import CycleStatusEnum from '../../../enums/cycle-status.enum';
import { PerChannelData } from '../../Cycle/types';
import { ScreenCycleWithId } from '../types/screen-cycle-table';

const THERMOCOUPLE_PREFIX = 'TC';

const getHighestState = (states: number[], isExistDisconnectedController: boolean) => {
  if (isExistDisconnectedController) {
    return ChannelState.FAULTED;
  }

  return Math.max(...states);
};

const getChannelsIotSummary = (modeNumber: CycleSettings, channelsIot: Record<string, PerChannelData>) => {
  if (modeNumber === CycleSettings.PostWeld) {
    let minStep: number | null = null;
    let exp: number | null = null;

    for (const channel of Object.values(channelsIot)) {
      const step = channel?.settingsByMode?.[CycleSettings.PostWeld]?.settings?.recipe?.currentStep;
      // const createdAt = channel?.settingsByMode?.[CycleSettings.PostWeld]?.settings?.recipe?.createdAt;
      // const expiration = channel?.settingsByMode?.[CycleSettings.PostWeld]?.settings?.recipe?.expiration;
      if (minStep === null || step < minStep) {
        minStep = step;
      }

      // const currentExp = Math.floor((Number(expiration) - Number(createdAt)) / 1000 / 60);
      // if (exp === null || currentExp < exp) {
      //   exp = currentExp;
      // }

      const minutesLeftInStep = channel?.iotData.minutesLeftInStep;
      if (exp === null || (minutesLeftInStep !== null && minutesLeftInStep > exp)) {
        exp = minutesLeftInStep;
      }
    }

    return {
      step: minStep ?? null,
      timeLeft: exp ?? null,
    };
  }

  if (modeNumber === CycleSettings.PreHeat) {
    let minFinalTemp: number | null = null;

    for (const channel of Object.values(channelsIot)) {
      const temp = channel?.settingsByMode?.[CycleSettings.PreHeat]?.settings?.finalTemperature;
      if (minFinalTemp === null || temp < minFinalTemp) {
        minFinalTemp = temp;
      }
    }

    return {
      step: 'PH',
      timeLeft: minFinalTemp ?? null,
    };
  }

  return {
    step: null,
    timeLeft: null,
  };
};

export const getCycleIotSummary = (cycle: ScreenCycleWithId) => {
  const isAllControllersDisconnected =
    !!cycle.cycle_controllers.length &&
    cycle.cycle_controllers.every((cycleController) => !cycleController.controller.connected);

  // Every channel is on a disconnected controller, so the cached
  // iotDataResponse is stale - show the same all-dashes state the
  // per-channel CycleTable rows already show instead of stale readings.
  if (isAllControllersDisconnected) {
    return {
      highestTemp: null,
      lowestTemp: null,
      highestSp: null,
      highestPresent: null,
      lowestPresent: null,
      minDeltaT: null,
      maxDeltaT: null,
      highestState: null,
      lowestMode: null,
      step: null,
      timeLeft: null,
    };
  }

  const sps: number[] = [];
  const temps: number[] = [];
  const presents: number[] = [];
  const deltaTs: number[] = [];
  const states: number[] = [];
  const modes: number[] = [];

  const isExistDisconnectedController = cycle.cycle_controllers.some(
    (cycleController) => !cycleController.controller.connected,
  );

  if (cycle.iotDataResponse && cycle.iotDataResponse.length) {
    cycle.iotDataResponse?.forEach((item) => {
      const { temp, sp, po, pk, state, serial, mode } = item;
      const isThermocouple = pk.includes(THERMOCOUPLE_PREFIX);
      temps.push(temp);
      if (isThermocouple) {
        return;
      }

      const isControllerFaulted = cycle.controllersSettings[serial]?.faults?.value.length;
      const isManualFaulted = item.manualFault !== null && item.manualFault !== undefined;
      const itemState = isManualFaulted || isControllerFaulted ? ChannelState.FAULTED : state;

      sps.push(sp);
      presents.push(po);
      // Same definition as CycleTable/utils.ts's getDeltaT: temp minus
      // setpoint, sign preserved (not present minus setpoint, and not
      // an absolute value) - so a channel running cold shows a negative
      // deltaT rather than being indistinguishable from running hot.
      deltaTs.push(temp - sp);
      states.push(itemState);
      modes.push(Number(mode));
    });
  }

  const highestTemp = temps.length ? Math.max(...temps) : null;
  const lowestTemp = temps.length ? Math.min(...temps) : null;
  const highestSp = sps.length ? Math.max(...sps) : null;
  const highestPresent = presents.length ? Math.max(...presents) : null;
  const lowestPresent = presents.length ? Math.min(...presents) : null;
  const minDeltaT = deltaTs.length ? Math.min(...deltaTs) : null;
  const maxDeltaT = deltaTs.length ? Math.max(...deltaTs) : null;
  const highestState = states.length ? getHighestState(states, isExistDisconnectedController) : null;
  const lowestMode = modes.length ? Math.max(...modes) : null;

  const channelDataByMode = getChannelsIotSummary(MODE_BY_NUMBER[lowestMode], cycle.channelsIot);

  const isRunning =
    cycle.status === CycleStatusEnum.Active &&
    highestState !== ChannelState.STOPPED &&
    highestState !== ChannelState.FAULTED;

  return {
    highestTemp,
    lowestTemp,
    highestSp,
    highestPresent,
    lowestPresent,
    minDeltaT,
    maxDeltaT,
    highestState,
    lowestMode,
    step: channelDataByMode.step,
    timeLeft: isRunning || MODE_BY_NUMBER[lowestMode] !== CycleSettings.PostWeld ? channelDataByMode.timeLeft : '-',
  };
};
